import type { Metadata } from "next";

/**
 * Lambang KATALOG RESELLER (`/toko/<kode>`) — etalase toko putih di ubin navy
 * platform, berkas statis di repo, dibuat ulang lewat
 * `node scripts/gen-katalog-icons.mjs`.
 *
 * ── Kenapa set sendiri, bukan `/icons/icon-*.png` ──────────────────────────
 * Katalog dulu memakai ikon STOREFRONT sebagai "ikon netral": ubin oranye toko
 * kanonik berlambang tas yang di pratinjau tautan WhatsApp terbaca sebagai
 * gembok. Permintaan pemilik 2026-10-30: katalog white-label memakai logo toko
 * yang universal, biru seperti platform (#27228F, kembar `platformIcons.ts`) —
 * bukan warna atau lambang toko induk mana pun.
 *
 * ⚠️ Nama berkas = versinya: `/icons/*` disajikan `immutable` setahun
 * (`next.config.mjs`). Mengganti gambarnya WAJIB lewat nama berkas baru.
 */

/** Favicon — full bleed, glyph dibesarkan agar terbaca di tab. */
export const CATALOG_ICON_32 = "/icons/katalog-icon-32.png";
/** Ubin rounded untuk tab beresolusi tinggi & manifest `purpose: any`. */
export const CATALOG_ICON_192 = "/icons/katalog-icon-192.png";
export const CATALOG_ICON_512 = "/icons/katalog-icon-512.png";
/** Full bleed, glyph di dalam safe zone Android — manifest `purpose: maskable`. */
export const CATALOG_MASKABLE_192 = "/icons/katalog-maskable-192.png";
export const CATALOG_MASKABLE_512 = "/icons/katalog-maskable-512.png";
/** Kotak penuh solid; iOS me-rounding sendiri. */
export const CATALOG_APPLE_TOUCH_ICON = "/icons/katalog-apple-touch-icon.png";
/** Gambar pratinjau tautan halaman katalog tanpa foto produk — kotak penuh tanpa alpha. */
export const CATALOG_OG_IMAGE = "/icons/katalog-og.png";

/**
 * Blok `icons` SETIAP halaman katalog — dipasang `src/app/toko/[kode]/layout.tsx`.
 * Ditulis UTUH: penggabungan metadata Next bersifat DANGKAL, jadi `icons`
 * parsial akan mewarisi sisa ikon toko induk dari layout root.
 */
export const CATALOG_METADATA_ICONS: Metadata["icons"] = {
  icon: [
    { url: CATALOG_ICON_32, sizes: "32x32", type: "image/png" },
    { url: CATALOG_ICON_192, sizes: "192x192", type: "image/png" },
    { url: CATALOG_ICON_512, sizes: "512x512", type: "image/png" },
  ],
  apple: [{ url: CATALOG_APPLE_TOUCH_ICON, sizes: "180x180" }],
};

/** Ikon manifest PWA katalog (`buildResellerManifest`). */
export const CATALOG_MANIFEST_ICONS: {
  src: string;
  sizes: string;
  type: string;
  purpose: "any" | "maskable";
}[] = [
  { src: CATALOG_ICON_192, sizes: "192x192", type: "image/png", purpose: "any" },
  { src: CATALOG_ICON_512, sizes: "512x512", type: "image/png", purpose: "any" },
  { src: CATALOG_MASKABLE_192, sizes: "192x192", type: "image/png", purpose: "maskable" },
  { src: CATALOG_MASKABLE_512, sizes: "512x512", type: "image/png", purpose: "maskable" },
];

/**
 * Entri `openGraph.images` halaman katalog tanpa foto produk. URL WAJIB
 * absolut (crawler pratinjau tak meresolusi path relatif); `origin` =
 * `catalogShareOrigin()`, alamat platform — bukan domain toko induk.
 */
export function catalogOgImage(origin: string): {
  url: string;
  width: number;
  height: number;
  type: string;
  alt: string;
} {
  const base = (origin ?? "").trim().replace(/\/+$/, "");
  return {
    url: `${base}${CATALOG_OG_IMAGE}`,
    width: 512,
    height: 512,
    type: "image/png",
    alt: "Katalog",
  };
}

/** Semua berkas lambang katalog — dipakai tes penahanan. */
export const CATALOG_ICON_FILES = [
  CATALOG_ICON_32,
  CATALOG_ICON_192,
  CATALOG_ICON_512,
  CATALOG_MASKABLE_192,
  CATALOG_MASKABLE_512,
  CATALOG_APPLE_TOUCH_ICON,
  CATALOG_OG_IMAGE,
] as const;
